import { useMemo, useState } from 'react';

import { PostFilter, type Keyword } from '../../../../../entities/post/models';
import { useHashtags } from '../../hooks';
import { useKeywords } from '../../hooks';
import { useFilteredPostsNavigation } from '../../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../../filtered-profiles/hook/use-filtered-profiles-navigation';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { ChartWordCloud, type ChartWordCloudBasis } from '@syncly/charts';
import { usePublicFilter } from '../../../shared/public-filter/use-public-filter';
import { showDrillDownContextMenu } from '@/features/(insight)/shared/overlay/show-drill-down-context-menu';

interface Props {
  className?: string;
}

enum TARGET_DATA {
  KEYWORDS = 'keywords',
  HASHTAGS = 'hashtags',
}

const WORD_CLOUD_BASIS_BY_TARGET_DATA = {
  [TARGET_DATA.KEYWORDS]: 'keywords',
  [TARGET_DATA.HASHTAGS]: 'hashtags',
} satisfies Record<TARGET_DATA, ChartWordCloudBasis>;

export function WordCloudSection({ className }: Props) {
  const [targetData, setTargetData] = useState<TARGET_DATA>(TARGET_DATA.KEYWORDS);
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(selectedQuery.id);

  const keywordsQuery = useKeywords(
    {
      queryId: selectedQuery.id,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      publicFilter,
    },
    targetData === TARGET_DATA.KEYWORDS,
  );

  const hashtagsQuery = useHashtags(
    {
      queryId: selectedQuery.id,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      publicFilter,
    },
    targetData === TARGET_DATA.HASHTAGS,
  );

  const currentQuery = useMemo(() => {
    if (targetData === TARGET_DATA.KEYWORDS) return keywordsQuery;
    return hashtagsQuery;
  }, [keywordsQuery, hashtagsQuery, targetData]);

  const words = useMemo(() => {
    return currentQuery.data
      ?.map((item) => ({
        keyword: item.name,
        count: item.post_volume,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }, [currentQuery]);

  const onRowClick = async (keyword: Keyword, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    const filterName =
      targetData === TARGET_DATA.KEYWORDS ? PostFilter.Keyword : PostFilter.Hashtag;

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter: {
            [filterName]: keyword.name,
          },
          dimension: filterName,
        });
        break;
      case 'view-profiles':
        await navigateToFilteredProfiles({
          filter: {
            [filterName]: keyword.name,
          },
          dimension: filterName,
        });
        break;
      case 'close':
        break;
    }
  };

  return (
    <div className={className}>
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="flex justify-between p-6 pb-0">
          <Select value={targetData} onValueChange={(value) => setTargetData(value as TARGET_DATA)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a word" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TARGET_DATA.KEYWORDS}>Keywords</SelectItem>
              <SelectItem value={TARGET_DATA.HASHTAGS}>Hashtags</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="h-104 w-full">
          <ChartWordCloud
            words={words ?? []}
            basis={WORD_CLOUD_BASIS_BY_TARGET_DATA[targetData]}
            height={416}
            isLoading={currentQuery.isLoading}
            onWordClick={(word, _, event) => {
              onRowClick(
                {
                  name:
                    targetData === TARGET_DATA.HASHTAGS ? word.text.replace(/^#/, '') : word.text,
                  post_volume: Math.floor(word.value),
                },
                event,
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
